import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import type { RefObject } from "react";
import { useId } from "react";
import { EXPIRY_ACK_ERROR } from "@/constants/review";

/**
 * The expiry acknowledgment and its inline error. Mirrors the prototype's
 * `.checkout-ack` / `#ackRow` + `#ackError`
 * (Campaign-Builder-Master-Prototype-Stage2-Revisions.html, markup ~line 11049,
 * CSS ~line 7960, `renderAckText()` ~line 23817, `_showAckError()` ~line 23963).
 *
 * Unchecking it never disables the CTA — clicking the CTA unacknowledged is
 * what surfaces `error` and pulls focus here, which is why the caller owns
 * `inputRef`.
 */
export interface ExpiryAckCheckboxProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Set by the CTA when it is clicked unacknowledged. */
  error: boolean;
  /** Copy varies with the rollover setting — see `expiryAckText()` in src/lib/checkout.ts. */
  text: string;
  /** So the CTA can pull focus to the checkbox when it raises the error. */
  inputRef?: RefObject<HTMLInputElement>;
}

export default function ExpiryAckCheckbox({
  checked,
  onChange,
  error,
  text,
  inputRef,
}: ExpiryAckCheckboxProps) {
  const errorId = useId();

  return (
    <>
      <label className="mt-4.5 flex cursor-pointer items-start gap-2.5 font-lato text-[13.5px] leading-normal text-rodeo-black">
        <input
          ref={inputRef}
          type="checkbox"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          aria-invalid={error}
          aria-describedby={error ? errorId : undefined}
          className={`mt-px h-[18px] w-[18px] shrink-0 cursor-pointer gfg-checkbox ${
            error ? "rounded-sm ring-3 ring-good-wine-4 outline outline-good-wine" : ""
          }`}
        />
        <span className="flex-1">{text}</span>
      </label>
      {error && (
        <p
          id={errorId}
          role="alert"
          className="mt-2 mb-0 flex items-center gap-1.5 font-lato text-[13px] leading-snug font-semibold text-good-wine"
        >
          <ErrorOutlineIcon className="shrink-0" sx={{ fontSize: 17 }} aria-hidden="true" />
          {EXPIRY_ACK_ERROR}
        </p>
      )}
    </>
  );
}
